import { cva } from "class-variance-authority";

/**
 * Link Button Variants
 *
 * V&A Waterfront Design System - Link Button
 * Figma: UI Components / Buttons / Link
 *
 * All color tokens reference semantic aliases that auto-switch via .dark:
 *   Text-Primary     → Semantics/Colours/Text/Primary
 *   Text-Secondary   → Semantics/Colours/Text/Secondary
 *   Text-Interactive  → Semantics/Colours/Text/Interactive (dark: Text/Primary)
 *   Text-Disabled    → Semantics/Colours/Text/Disabled
 *   Stroke-Primary   → Semantics/Colours/Borders/Colours/Interactive
 *   Stroke-Interactive → Semantics/Colours/Borders/Colours/Interactive
 *   Stroke-Secondary → Semantics/Colours/Icons/Base/Secondary
 *   Stroke-Disabled  → Semantics/Colours/Borders/Colours/Disabled
 *   Icon-Primary     → Semantics/Colours/Icons/Base/Primary
 *   Icon-Secondary   → Semantics/Colours/Icons/Base/Secondary
 *   Icon-Interactive  → Semantics/Colours/Icons/Base/Interactive
 *   Icon-Disabled    → Semantics/Colours/Icons/Base/Disabled
 *
 * Underline mechanism: `background-image` linear-gradient stripe + `box-decoration-break: clone`.
 *
 * Why not `text-decoration: underline`? When this LinkButton is rendered via
 * `asChild` (Radix Slot) inside a blockified flex item parent — the common
 * case across the project — Chrome stops painting text-decoration on lines
 * beyond the first. The `background-image` approach paints reliably per
 * fragment via `box-decoration-break: clone`.
 *
 * Why `display: inline` (not inline-flex)? `box-decoration-break: clone`
 * clones the background per inline fragment, which only happens when the
 * box is fragmentable — i.e. `display: inline`. Icons render as inline-block
 * siblings aligned via `vertical-align`; spacing is handled per-icon margin
 * instead of flex `gap`.
 *
 * Position: the stripe is positioned 8px below the line bottom via
 * `padding-bottom: 0.5rem`, matching the original Figma `border-b + py-1`
 * visual position. Extra `line-height: 1.4` gives line 2 enough leading to
 * not collide with line 1's underline when text wraps.
 *
 * Where the previous `border-b + py-N` was load-bearing for structural
 * alignment with a sibling divider (e.g. forming one continuous line), the
 * consumer should apply its own `no-underline border-b` override at the call
 * site — see `audience-tabs.component.tsx`.
 *
 * - true: stripe visible (decoration-border-interactive colour)
 * - false: stripe transparent, becomes visible on hover/focus
 */

// 1px-tall linear-gradient stripe at the bottom of each inline fragment's
// line-box (no padding-bottom). With `display: inline` + half-leading from
// `line-height: 1.5`, the line-box bottom sits ~lh/2 below the baseline,
// which matches the Figma `border-b + py-1 + items-center` position
// (~13px below baseline for lg) without any extra padding pushing the
// stripe further from the text. `--lb-underline` is set by the underline
// variant.
const underlineStripe = [
  "[-webkit-box-decoration-break:clone] [box-decoration-break:clone]",
  "bg-[image:linear-gradient(var(--lb-underline,transparent),var(--lb-underline,transparent))]",
  "bg-no-repeat bg-[length:100%_1px] bg-[position:0_100%]",
];

export const linkButtonVariants = cva(
  // Base styles
  [
    // `display: inline` is required for the background-image stripe to clone
    // per visual line via `box-decoration-break: clone`.
    "inline",
    "transition-[background-image,color] cursor-pointer",
    "focus:outline-none",
    "disabled:pointer-events-none",
    "data-[disabled]:pointer-events-none",
    // SVG icons positioned inline-block so they flow with the wrapping text
    // and sit on the same baseline as the inline LinkButton element.
    "[&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:inline-block [&_svg]:align-text-bottom",
    "[&_svg:first-child]:mr-2 [&_svg:last-child]:ml-2",
    "disabled:[&_svg]:text-icon-disabled",
    "data-[disabled]:[&_svg]:text-icon-disabled",
    ...underlineStripe,
  ],
  {
    variants: {
      variant: {
        // Primary - Main inline links
        primary: [
          "text-text-primary [&_svg]:text-icon-primary",
          "hover:text-text-interactive hover:[&_svg]:text-icon-interactive",
          "focus-visible:text-text-interactive focus-visible:[&_svg]:text-icon-interactive",
          "disabled:text-text-disabled",
          "data-[disabled]:text-text-disabled",
        ],
        // Secondary - Less prominent links
        secondary: [
          "text-text-secondary [&_svg]:text-icon-secondary",
          "hover:text-text-interactive hover:[&_svg]:text-icon-interactive",
          "focus-visible:text-text-interactive focus-visible:[&_svg]:text-icon-interactive",
          "disabled:text-text-disabled",
          "data-[disabled]:text-text-disabled",
        ],
      },
      size: {
        // Large - 18px. `leading-[1.5]` puts the line-box bottom (where the
        // stripe lives) ~13px below the baseline — matching Figma's
        // `border-b + py-1 + items-center` position — while giving line 2's
        // cap-letters enough clearance to not collide with line 1's stripe
        // on wrap.
        lg: "typography-component-md-medium leading-[1.5]",
        // Medium - 16px. Same reasoning.
        md: "typography-component-sm-medium leading-[1.5]",
        // Small - 14px. Same reasoning.
        sm: "typography-component-xs-medium leading-[1.5]",
      },
      underline: {
        // Stripe visible. Uses the semantic tokens (no `--color-` prefix) so
        // the value auto-switches white inside `.dark` containers. The
        // `--color-*` Tailwind tokens are compiled at build time and do NOT
        // react to `.dark` scope, so we reference the underlying semantic
        // variables directly here.
        true: [
          "[--lb-underline:var(--border-interactive)]",
          "disabled:[--lb-underline:var(--border-disabled)]",
          "data-[disabled]:[--lb-underline:var(--border-disabled)]",
        ],
        // Stripe transparent until hover/focus
        false: [
          "[--lb-underline:transparent]",
          "hover:[--lb-underline:var(--border-interactive)]",
          "focus-visible:[--lb-underline:var(--border-interactive)]",
        ],
      },
    },
    compoundVariants: [
      // Secondary variant with underline visible uses the secondary stroke
      // colour token to match the muted text colour.
      {
        variant: "secondary",
        underline: true,
        className: [
          "[--lb-underline:var(--icon-secondary)]",
          "hover:[--lb-underline:var(--border-interactive)]",
          "focus-visible:[--lb-underline:var(--border-interactive)]",
        ],
      },
    ],
    defaultVariants: {
      variant: "primary",
      size: "lg",
      underline: false,
    },
  },
);
